import { Instagram, Facebook } from "lucide-react";
import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";

const quotes = [
  { quote: "I'm gonna make him a gift basket he can't refuse.", source: "The Godfather" },
  { quote: "Frankly, my dear, I don't give a gift wrap.", source: "Gone with the Wind" },
  { quote: "There's no place like the gift shop.", source: "The Wizard of Oz" },
  { quote: "May the Gift be with you.", source: "Star Wars" },
  { quote: "I'll be back... with the gift receipt.", source: "The Terminator" },
  { quote: "You're gonna need a bigger gift bag.", source: "Jaws" },
  { quote: "Go ahead, make my gift.", source: "Sudden Impact" },
  { quote: "There's no crying in gift-giving!", source: "A League of Their Own" },
  { quote: "You can't handle the gift!", source: "A Few Good Men" },
  { quote: "I see gift cards.", source: "The Sixth Sense" },
  { quote: "Say hello to my little gift!", source: "Scarface" },
  { quote: "I will find you, and I will gift you.", source: "Taken" },
  { quote: "Life is like a box of gifts—you never know which one you're gonna get.", source: "Forrest Gump" },
  { quote: "Show me the gift!", source: "Jerry Maguire" },
  { quote: "You had me at 'I got you a gift.'", source: "Jerry Maguire" },
  { quote: "The first rule of Gift Club is: You do not talk about the price tag.", source: "Fight Club" },
  { quote: "My precious... gift!", source: "The Lord of the Rings" },
  { quote: "Why so serious? Have a gift!", source: "The Dark Knight" },
  { quote: "I'll have what she's gifting.", source: "When Harry Met Sally" },
  { quote: "On Wednesdays, we open gifts.", source: "Mean Girls" },
  { quote: "To the gift shop... and beyond!", source: "Toy Story" },
  { quote: "I am the one who gifts!", source: "Breaking Bad" },
  { quote: "I'm also just a girl, standing in front of a boy, asking him for a gift.", source: "Notting Hill" },
  { quote: "I'll never let go of this present Jack.", source: "Titanic" },
  { quote: "You get a gift, everyone gets a gift!", source: "Oprah" },
];

const Footer = () => {
  const [index, setIndex] = useState(() => Math.floor(Math.random() * quotes.length));

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prev) => (prev + 1) % quotes.length);
    }, 15000);
    return () => clearInterval(interval);
  }, []);

  return (
    <footer
      className="border-t border-border mt-auto bg-cover bg-center bg-no-repeat relative"
      style={{ backgroundImage: "url('/images/footer-banner.webp')", contentVisibility: "auto", containIntrinsicSize: "auto 400px" } as React.CSSProperties}
    >
      {/* Dark overlay behind everything */}
      <div className="absolute inset-0 bg-black/60" />

      {/* Gift Wrapping Ribbon */}
      <div className="relative z-10 w-full py-6 flex items-center justify-center overflow-hidden">
        {/* Left twirl/curl SVG */}
        <svg aria-hidden="true" className="absolute left-0 top-1/2 -translate-y-1/2" width="90" height="80" viewBox="0 0 90 80" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.15))" }}>
          <path d="M90 36 C70 36, 40 18, 28 28 C16 38, 30 56, 44 50 C58 44, 35 22, 18 34 C6 43, 12 58, 28 54" stroke="url(#ribbonCurlL)" strokeWidth="4" fill="none" strokeLinecap="round"/>
          <defs>
            <linearGradient id="ribbonCurlL" x1="90" y1="40" x2="10" y2="40">
              <stop offset="0%" stopColor="#ebebeb"/>
              <stop offset="100%" stopColor="#c8c8c8" stopOpacity="0.3"/>
            </linearGradient>
          </defs>
        </svg>

        {/* Right twirl/curl SVG */}
        <svg aria-hidden="true" className="absolute right-0 top-1/2 -translate-y-1/2" width="90" height="80" viewBox="0 0 90 80" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.15))" }}>
          <path d="M0 36 C20 36, 50 18, 62 28 C74 38, 60 56, 46 50 C32 44, 55 22, 72 34 C84 43, 78 58, 62 54" stroke="url(#ribbonCurlR)" strokeWidth="4" fill="none" strokeLinecap="round"/>
          <defs>
            <linearGradient id="ribbonCurlR" x1="0" y1="40" x2="80" y2="40">
              <stop offset="0%" stopColor="#ebebeb"/>
              <stop offset="100%" stopColor="#c8c8c8" stopOpacity="0.3"/>
            </linearGradient>
          </defs>
        </svg>

        {/* Left V-cut end */}
        <div className="absolute left-[20px] top-1/2 -translate-y-1/2 z-10" style={{ filter: "drop-shadow(-2px 2px 3px rgba(0,0,0,0.15))" }}>
          <svg width="20" height="48" viewBox="0 0 20 48" fill="none">
            <polygon points="20,0 0,0 10,24 0,48 20,48" fill="url(#vcutL)" />
            <defs>
              <linearGradient id="vcutL" x1="0" y1="0" x2="0" y2="48">
                <stop offset="0%" stopColor="#f5f5f5"/>
                <stop offset="50%" stopColor="#ffffff"/>
                <stop offset="100%" stopColor="#ececec"/>
              </linearGradient>
            </defs>
          </svg>
        </div>

        {/* Right V-cut end */}
        <div className="absolute right-[20px] top-1/2 -translate-y-1/2 z-10" style={{ filter: "drop-shadow(2px 2px 3px rgba(0,0,0,0.15))" }}>
          <svg width="20" height="48" viewBox="0 0 20 48" fill="none">
            <polygon points="0,0 20,0 10,24 20,48 0,48" fill="url(#vcutR)" />
            <defs>
              <linearGradient id="vcutR" x1="0" y1="0" x2="0" y2="48">
                <stop offset="0%" stopColor="#f5f5f5"/>
                <stop offset="50%" stopColor="#ffffff"/>
                <stop offset="100%" stopColor="#ececec"/>
              </linearGradient>
            </defs>
          </svg>
        </div>

        {/* Main ribbon body */}
        <div
          className="relative w-[calc(100%-80px)] mx-10 py-4 z-20"
          style={{
            background: "linear-gradient(180deg, #f8f8f8 0%, #ffffff 20%, #f0f0f0 35%, #ffffff 50%, #f0f0f0 65%, #ffffff 80%, #f5f5f5 100%)",
            boxShadow: "0 6px 20px rgba(0,0,0,0.2), 0 -3px 10px rgba(0,0,0,0.08), inset 0 2px 0 rgba(255,255,255,1), inset 0 -2px 0 rgba(0,0,0,0.04)",
          }}
        >
          {/* Satin sheen */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: "linear-gradient(110deg, transparent 0%, rgba(255,255,255,0.6) 15%, transparent 30%, rgba(255,255,255,0.4) 50%, transparent 65%, rgba(255,255,255,0.5) 80%, transparent 100%)",
            }}
          />
          {/* Subtle edge lines */}
          <div className="absolute top-[4px] left-4 right-4 border-t border-gray-200/40" />
          <div className="absolute bottom-[4px] left-4 right-4 border-t border-gray-200/40" />

          <div className="relative flex justify-center items-center min-h-[2rem]">
            <AnimatePresence mode="wait">
              <motion.p
                key={index}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.5 }}
                className="text-sm italic text-center max-w-lg font-medium text-gray-700 px-8 tracking-wide"
              >
                "{quotes[index].quote}" — <span className="font-semibold">{quotes[index].source}</span>
              </motion.p>
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Footer Content */}
      <div className="relative z-10">
        <div className="container max-w-7xl mx-auto px-4 py-10">
          <div className="flex justify-between">
            {/* Brand */}
            <div>
              <p className="text-base font-semibold text-white mb-2">HINTSLY</p>
              <p className="text-sm text-white/90">ABN 67 350 155 468</p>
              <p className="text-sm text-white/90 mt-1">™ Hintsly is a registered trademark.</p>
              <p className="text-sm text-white/90 mt-1">Give the gift they'll actually love.</p>
            </div>

            {/* Social */}
            <div className="flex flex-col gap-3 text-right">
              <p className="text-base font-medium text-white">Follow Us</p>
              <div className="flex gap-3 justify-end">
                <a
                  href="https://instagram.com/hintslyau"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-lg bg-white/10 text-white/90 hover:text-white hover:bg-white/20 transition-colors"
                  aria-label="Instagram"
                >
                  <Instagram className="h-5 w-5" />
                </a>
                <a
                  href="https://facebook.com/hintslyau"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-lg bg-white/10 text-white/90 hover:text-white hover:bg-white/20 transition-colors"
                  aria-label="Facebook"
                >
                  <Facebook className="h-5 w-5" />
                </a>
              </div>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-white/30 text-center">
            <p className="text-sm text-white/80">
              © {new Date().getFullYear()} Hintsly. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
